import { useEffect, useMemo, useState } from 'react';
import { AppShell, Card, } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { Outlet } from 'react-router-dom';
// import { formatBuildString } from '../../app/helpers';
import { RootState } from '../../app/RootState';
import { selectCurrentRefreshToken, selectCurrentToken } from '../../app/slices/authSlice';
import { setLoginAllRoles, setLoginRole } from '../../app/slices/companySlice';
import { useAppDispatch } from '../../app/store';
import { useGetRoleMenuMappingByRoleIdQuery } from '../../app/api/configurationApiHandler';
import { useGetroleQuery } from '../../app/api/rolesApiHandler';
import { roleLevels } from '../../constants/Constant';
import { useHighestUserRole } from '../../components/useHighestUserRole';
import { UsersRoles } from '../../types/users';
// import { LayoutFooterWithSkeleton } from '../../components/SkeletonLoading';
// import classes from '../../Global.module.css';
import AppLoader from '../../components/AppLoader';
import Header from './Header/Header';
import './Layout.css';
import Navbar from './Navbar/Navbar';
import type { NavItem } from './Navbar/Navbar';
// import CustomDivider from '../../components/ui/CustomDivider/CustomDivider';
import UserMenu from './Header/UserMenu';
import { sideNavbarcolor } from '../../components/mantineReactTable/sizes';
import QuickActionsFAB from '../../components/ui/QuickActionsFAB/QuickActionsFAB';
import { useGetEmailByIndividualIdQuery } from '../../app/api/usersApiHandler';

export interface UserDetails {
  id: string;
  primaryEmail: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  fullName: string;
}

export interface LayoutRouteContext {
  menuItems: NavItem[];
  isLoading: boolean;
  isError: boolean;
  usersData: UserDetails;
}

// const menuAllowsPath = (items: NavItem[], targetPath: string): boolean =>
//   items.some((item) => {
//     const menuPath = item.navMenuUrl.replace(/^\/+|\/+$/g, '').toLowerCase();
//     const isActiveAncestor =
//       item.isActive === true &&
//       (targetPath === menuPath || targetPath.startsWith(`${menuPath}/`));

//     return isActiveAncestor || menuAllowsPath(item.childMenus ?? [], targetPath);
//   });

export function Layout() {
  // const showLoginLoader = sessionStorage.getItem('showLoginLoader') === 'true';
  // const [isSkeleton, setIsSkeleton] = useState(showLoginLoader);
  const dispatch = useAppDispatch();
  // const year = new Date().getFullYear();

  const token = useSelector(selectCurrentToken);
  const isAuthorized = Boolean(token || sessionStorage.getItem('token'));
  const { highestRole } = useHighestUserRole();
  const { data: roleData, isLoading: isRoleLoading, isError: isRoleError, } = useGetroleQuery({}, { skip: !isAuthorized });
  const noRoleId = '00000000-0000-0000-0000-000000000000';
  const roleId = roleData?.data?.find((role: UsersRoles) => role.name === highestRole)?.id ?? noRoleId;
  const { data: roleMapData, isLoading: isRoleMenuLoading, isError: isRoleMenuError, } = useGetRoleMenuMappingByRoleIdQuery(
    { id: roleId },
    { skip: !isAuthorized || roleId === noRoleId },
  );


  const emailId = sessionStorage.getItem('LoggedUserEmail');

  const { data: UsersData, isLoading: isUsersLoading } = useGetEmailByIndividualIdQuery({
    primaryEmail: emailId,
  });

  const menuItems: NavItem[] = useMemo(() => roleMapData?.data?.menuItems || [],
    [roleMapData?.data?.menuItems],
  );
  // const canAccessMyProfile = menuAllowsPath(menuItems, 'people/profile');
  const routeContext: LayoutRouteContext = {
    menuItems,
    isLoading: isRoleLoading || isRoleMenuLoading || isUsersLoading,
    isError: isRoleError || isRoleMenuError,
    usersData: UsersData?.data[0]
  };

  const storedRole = useSelector((state: RootState) => state.company.loginRole) || sessionStorage.getItem('Role');
  const [initializedToken, setInitializedToken] = useState<string | null>(null);
  const refreshToken = useSelector(selectCurrentRefreshToken);
  let IsAuthenticated = false;
  if (token != null && refreshToken != null) {
    IsAuthenticated = true;
  }
  // const buildNumber = import.meta.env.VITE_DOC_BUILD_NUMBER || 'N/A'; 
  const isMobile = useMediaQuery('(max-width: 576px)');
  //     const isMobileOrIPadMini = useMediaQuery(
  //   `only screen and (max-width: 768px),
  //    (min-device-width: 768px) and (max-device-width: 834px) and (-webkit-min-device-pixel-ratio: 2)`
  // );
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  useEffect(() => {
    if (!token) {
      setInitializedToken(null);
      return;
    }

    try {
      const decodedToken: { [key: string]: string | string[] | undefined } = jwtDecode(token);
      const roles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const normalizedRoles = (Array.isArray(roles) ? roles : roles ? [roles] : [])
        .filter((role): role is string => typeof role === 'string');
      const validRoles = normalizedRoles.filter((role) => role in roleLevels);
      const selectedRole = storedRole && validRoles.includes(storedRole)
        ? storedRole
        : validRoles.reduce<string | null>((highest, role) =>
          highest === null || roleLevels[role] < roleLevels[highest] ? role : highest,
          null);

      dispatch(setLoginAllRoles(normalizedRoles));
      sessionStorage.setItem('AllRoles', JSON.stringify(normalizedRoles));
      if (selectedRole) {
        dispatch(setLoginRole(selectedRole));
        sessionStorage.setItem('Role', selectedRole);
      }
    } catch (error) {
      console.error('Failed to initialize user roles from the access token.', error);
    } finally {
      setInitializedToken(token);
    }
  }, [dispatch, storedRole, token]);


  const isEnable = isMobile ? mobileOpened : desktopOpened;
  const bgColor = sideNavbarcolor

  return (
    <>
      {IsAuthenticated ? (
        <AppShell
          {...(isMobile ? { header: { height: 60 } } : {})}
          footer={{ height: UsersData?.data[0]?.fullName?.length > 20 ? 70 : 50 }}
          navbar={{ width: !desktopOpened ? 50 : 220, breakpoint: 'xs', collapsed: { mobile: !mobileOpened, desktop: false } }}
          // {...(pathname === '/home' ? { aside: { width: 450, breakpoint: 'sm', collapsed: { desktop: isCompactHomeLayout, mobile: true } } } : {})}
          padding="sm"
        //  layout="alt"
        >
          {/* Header Component */}
          {isMobile &&
            <AppShell.Header bg={bgColor}>
              <Header
                mobileOpened={mobileOpened}
                desktopOpened={desktopOpened}
                toggleMobile={toggleMobile}
                toggleDesktop={toggleDesktop}
                isEnable={isEnable}
                isEnableToolBar={true}
                isMobile={isMobile}
                // canAccessMyProfile={canAccessMyProfile}
                roleDefinitions={roleData?.data ?? []}
              />
            </AppShell.Header>}
          {/* Navbar Component */}
          <AppShell.Navbar bg={bgColor}>
            {!isMobile &&
              <Header
                mobileOpened={mobileOpened}
                desktopOpened={desktopOpened}
                toggleMobile={toggleMobile}
                toggleDesktop={toggleDesktop}
                isEnable={isEnable}
                isMobile={isMobile}
                roleDefinitions={roleData?.data ?? []}
              />}
            <Navbar
              // isSkeleton={isSkeleton}
              mobileOpened={mobileOpened}
              desktopOpened={isEnable}
              toggleMobile={toggleMobile}
              menuItems={menuItems}
              isMenuLoading={routeContext.isLoading}
            />
          </AppShell.Navbar>

          {routeContext.isLoading ? <AppLoader /> :
            <AppShell.Main bg={'#f8fafc80'}>
              {initializedToken === token ? <Outlet context={routeContext} /> : <AppLoader />}
            </AppShell.Main>}


          {!isMobile &&
            <AppShell.Footer w={desktopOpened ? 220 : 50} bg={bgColor}  >
              {/* <CustomDivider /> */}
              <Card p={5} h={70} radius={0} bg={bgColor} >
                <UserMenu
                  fullName={UsersData?.data[0]?.fullName ?? ''}
                  isEnableIconOnly={desktopOpened}
                  // canAccessMyProfile={canAccessMyProfile}
                  roleDefinitions={roleData?.data ?? []}
                />
              </Card>

            </AppShell.Footer>}
          {IsAuthenticated && <QuickActionsFAB menuItems={menuItems} isLoading={routeContext.isLoading} />}
        </AppShell>
      ) : (
        <Outlet context={routeContext} />
      )}
    </>
  );
}